import { Observable, Subscription } from 'rxjs';

import {
  DependentComponentsOperationalData,
  DirectDependencySummary,
  OperationalEnvelope,
  ResourceGraphNode,
  ResourceGraphOperationalData,
  ResourceGraphReference,
  ResourceGraphTopologyManifestOperationalData,
} from './tenant-operations.service';

export type DependencyTransportState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export interface DependencyEvidenceState {
  state: DependencyTransportState;
  summaries: DirectDependencySummary[];
  error: string | null;
}

export interface ResourceGraphStoreLoaders {
  manifest?(): Observable<OperationalEnvelope<ResourceGraphTopologyManifestOperationalData>>;
  componentDetail(componentId: string): Observable<OperationalEnvelope<ResourceGraphOperationalData>>;
  dependentComponents(componentId: string): Observable<OperationalEnvelope<DependentComponentsOperationalData>>;
}

interface ComponentDetailState {
  state: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  subscription?: Subscription;
}

interface DependentComponentState {
  state: DependencyTransportState;
  error: string | null;
  subscription?: Subscription;
}

/** Canonical Resource Graph evidence for one Operational View generation. */
export class OperationalViewResourceGraphStore {
  private readonly records = new Map<string, ResourceGraphNode>();
  private readonly ownership = new Map<string, ResourceGraphReference[]>();
  private readonly constructs = new Map<string, ResourceGraphNode[]>();
  private readonly outgoingReferences = new Map<string, ResourceGraphReference[]>();
  private readonly inboundReferences = new Map<string, ResourceGraphReference[]>();
  private readonly componentDetails = new Map<string, ComponentDetailState>();
  private readonly dependentComponents = new Map<string, DependentComponentState>();
  private manifestSubscription?: Subscription;
  private generation = 0;
  private invalidated = false;

  manifestResult: OperationalEnvelope<ResourceGraphTopologyManifestOperationalData> | null = null;
  manifestLoading = false;
  manifestRefreshing = false;
  manifestError: string | null = null;
  manifestVersion = 0;
  rootComponentId: string | null = null;
  detachedRootComponentIds: string[] = [];

  constructor(
    manifest: ResourceGraphTopologyManifestOperationalData | null,
    private readonly loaders?: ResourceGraphStoreLoaders,
    private readonly changed: () => void = () => {},
  ) {
    if (manifest) this.seedManifest(manifest);
  }

  loadManifest(replacing = false): void {
    if (!this.loaders?.manifest || this.invalidated) return;
    this.clearGeneration();
    const requestGeneration = this.generation;
    this.manifestLoading = !replacing;
    this.manifestRefreshing = replacing;
    this.manifestError = null;
    this.manifestResult = null;
    this.changed();
    this.manifestSubscription = this.loaders.manifest().subscribe({
      next: (result) => {
        if (!this.isCurrent(requestGeneration)) return;
        this.manifestResult = result;
        if (result.status === 'ready' && result.data) {
          this.seedManifest(result.data);
        } else {
          this.manifestError = result.error?.message ?? 'Resource Graph Topology Manifest could not be retrieved';
        }
        this.manifestLoading = false;
        this.manifestRefreshing = false;
        this.changed();
      },
      error: () => {
        if (!this.isCurrent(requestGeneration)) return;
        this.manifestResult = null;
        this.manifestError = 'Resource Graph Topology Manifest could not be retrieved';
        this.manifestLoading = false;
        this.manifestRefreshing = false;
        this.changed();
      },
    });
  }

  private seedManifest(manifest: ResourceGraphTopologyManifestOperationalData): void {
    this.rootComponentId = manifest.named_root_component_id;
    this.detachedRootComponentIds = manifest.detached_root_component_ids;
    manifest.components.forEach((component) => this.records.set(component.component_id, {
      id: component.component_id,
      kind: 'component',
      component_id: component.component_id,
      tenant_id: manifest.tenant_id,
      native_type: component.native_type,
      state: component.state,
      root_component_id: component.root_component_id,
      tenant_solution_scope: component.tenant_solution_scope,
      branch_capable: true,
      identifiers: { component_id: component.component_id },
      references: [],
      properties: {},
      raw: [],
    }));
    manifest.ownership_edges.forEach((edge) => this.addOwnershipReference({
      relationship_type: 'ownership',
      source_component_id: edge.source_component_id,
      target_component_id: edge.target_component_id,
      target_native_type: edge.target_native_type ?? '',
      raw: {},
    }));
    this.manifestVersion++;
  }

  invalidate(): void {
    if (this.invalidated) return;
    this.invalidated = true;
    this.clearGeneration();
  }

  node(id: string): ResourceGraphNode | null { return this.records.get(id) ?? null; }
  component(componentId: string): ResourceGraphNode | null { return this.records.get(componentId) ?? null; }
  ownershipReferences(componentId: string): ResourceGraphReference[] { return this.ownership.get(componentId) ?? []; }
  constructsFor(componentId: string): ResourceGraphNode[] { return this.constructs.get(componentId) ?? []; }
  dependencyReferences(componentId: string): ResourceGraphReference[] { return this.outgoingReferences.get(componentId) ?? []; }
  detailState(componentId: string): ComponentDetailState['state'] { return this.componentDetails.get(componentId)?.state ?? 'idle'; }
  detailError(componentId: string): string | null { return this.componentDetails.get(componentId)?.error ?? null; }

  loadComponentDetail(componentId: string, force = false): void {
    const existing = this.componentDetails.get(componentId);
    if (!this.loaders || this.invalidated || !this.records.has(componentId)
      || (!force && (existing?.state === 'loading' || existing?.state === 'ready'))) return;
    const state: ComponentDetailState = { state: 'loading', error: null };
    this.runGenerationScopedLoad(
      this.componentDetails,
      componentId,
      state,
      () => this.loaders!.componentDetail(componentId),
      (result) => {
        if (result.status === 'ready' && result.data) {
          this.mergeComponentDetail(result.data);
          state.state = 'ready';
        } else {
          state.state = 'error';
          state.error = result.error?.message ?? `Component ${componentId} could not be retrieved`;
        }
      },
      () => {
        state.state = 'error';
        state.error = `Component ${componentId} could not be retrieved`;
      },
    );
  }

  loadDependentComponents(componentId: string, force = false): void {
    const existing = this.dependentComponents.get(componentId);
    if (!this.loaders || this.invalidated || !this.records.has(componentId)
      || (!force && existing && existing.state !== 'error')) return;
    const state: DependentComponentState = { state: 'loading', error: null };
    this.runGenerationScopedLoad(
      this.dependentComponents,
      componentId,
      state,
      () => this.loaders!.dependentComponents(componentId),
      (result) => {
        if (result.status === 'ready' && result.data) {
          this.setDependentReferences(componentId, result.data.references);
          state.state = result.data.references.length ? 'ready' : 'empty';
        } else {
          state.state = 'error';
          state.error = result.error?.message ?? 'Dependent Components could not be retrieved';
        }
      },
      () => {
        state.state = 'error';
        state.error = 'Dependent Components could not be retrieved';
      },
    );
  }

  private runGenerationScopedLoad<TState extends { subscription?: Subscription }, TResult>(
    states: Map<string, TState>,
    componentId: string,
    state: TState,
    request: () => Observable<TResult>,
    received: (result: TResult) => void,
    failed: () => void,
  ): void {
    states.get(componentId)?.subscription?.unsubscribe();
    const requestGeneration = this.generation;
    states.set(componentId, state);
    this.changed();
    state.subscription = request().subscribe({
      next: (result) => {
        if (!this.isCurrent(requestGeneration)) return;
        received(result);
        this.changed();
      },
      error: () => {
        if (!this.isCurrent(requestGeneration)) return;
        failed();
        this.changed();
      },
    });
  }

  mergeComponentDetail(data: ResourceGraphOperationalData): void {
    const manifestRecord = this.records.get(data.component.component_id);
    if (!manifestRecord) return;
    this.records.set(data.component.component_id, {
      ...data.component,
      component_id: manifestRecord.component_id,
      tenant_id: manifestRecord.tenant_id,
      native_type: manifestRecord.native_type ?? data.component.native_type,
      state: manifestRecord.state ?? data.component.state,
      root_component_id: manifestRecord.root_component_id,
      tenant_solution_scope: manifestRecord.tenant_solution_scope,
    });
    this.outgoingReferences.set(data.component.component_id, data.dependencies);
    this.constructs.set(data.component.component_id, data.constructs);
    data.constructs.forEach((construct) => this.records.set(construct.id, construct));
  }

  setDependentReferences(componentId: string, references: ResourceGraphReference[]): void {
    this.inboundReferences.set(componentId, references);
  }

  hasOutgoingReferences(componentId: string): boolean { return this.outgoingReferences.has(componentId); }

  outgoing(componentId: string, error: string | null = null): DependencyEvidenceState {
    const detail = this.componentDetails.get(componentId);
    if (error || detail?.state === 'error') return { state: 'error', summaries: [], error: error ?? detail?.error ?? null };
    const references = this.outgoingReferences.get(componentId);
    if (!references) return { state: detail?.state === 'idle' ? 'idle' : 'loading', summaries: [], error: null };
    return this.summaryState(references, 'target_component_id');
  }

  inbound(componentId: string, state?: DependencyTransportState, error: string | null = null): DependencyEvidenceState {
    const load = this.dependentComponents.get(componentId);
    const resolvedState = state ?? load?.state ?? 'idle';
    if (resolvedState === 'error') return { state: resolvedState, summaries: [], error: error ?? load?.error ?? null };
    const references = this.inboundReferences.get(componentId);
    if (!references) return { state: resolvedState, summaries: [], error: null };
    return this.summaryState(references, 'source_component_id');
  }

  route(componentId: string): string[] | null {
    if (!this.records.has(componentId)) return null;
    const componentIds = [componentId];
    const visited = new Set(componentIds);
    let currentComponentId = componentId;
    while (true) {
      const owners = [...this.ownership.entries()].filter(([, references]) =>
        references.some((reference) => reference.target_component_id === currentComponentId)
      );
      if (!owners.length) {
        const rootId = componentIds.at(-1);
        return rootId === this.rootComponentId || this.detachedRootComponentIds.includes(rootId ?? '') ? componentIds.reverse() : null;
      }
      if (owners.length !== 1) return null;
      currentComponentId = owners[0][0];
      if (visited.has(currentComponentId)) return null;
      visited.add(currentComponentId);
      componentIds.push(currentComponentId);
    }
  }

  private addOwnershipReference(reference: ResourceGraphReference): void {
    this.ownership.set(reference.source_component_id, [...(this.ownership.get(reference.source_component_id) ?? []), reference]);
  }
  private clearGeneration(): void {
    this.generation++;
    this.manifestSubscription?.unsubscribe();
    this.componentDetails.forEach(({ subscription }) => subscription?.unsubscribe());
    this.dependentComponents.forEach(({ subscription }) => subscription?.unsubscribe());
    this.records.clear();
    this.ownership.clear();
    this.constructs.clear();
    this.outgoingReferences.clear();
    this.inboundReferences.clear();
    this.componentDetails.clear();
    this.dependentComponents.clear();
    this.rootComponentId = null;
    this.detachedRootComponentIds = [];
  }
  private isCurrent(requestGeneration: number): boolean { return !this.invalidated && requestGeneration === this.generation; }
  private summaryState(references: ResourceGraphReference[], endpoint: 'source_component_id' | 'target_component_id'): DependencyEvidenceState {
    const summaries = references.map((reference) => this.summary(reference, endpoint));
    return { state: summaries.length ? 'ready' : 'empty', summaries, error: null };
  }
  private summary(reference: ResourceGraphReference, endpoint: 'source_component_id' | 'target_component_id'): DirectDependencySummary {
    const target = this.records.get(reference[endpoint]);
    if (!target) return {
      source_reference: reference, status: 'unavailable', target: null, source_solution_scope: null, target_solution_scope: null,
      solution_relationship: 'unavailable', error: { code: 'manifest_endpoint_not_found', message: `Component ${reference[endpoint]} is not present in the Resource Graph Topology Manifest.` },
    };
    const source = this.records.get(reference.source_component_id);
    const relationshipTarget = this.records.get(reference.target_component_id);
    const relationship = relationshipTarget ? this.relationship(source, relationshipTarget) : 'unavailable';
    return {
      source_reference: reference, status: relationship === 'unavailable' ? 'unavailable' : 'ready',
      target: { component_id: target.component_id, native_type: target.native_type, state: target.state, raw: target.raw },
      source_solution_scope: source?.tenant_solution_scope ?? null, target_solution_scope: relationshipTarget?.tenant_solution_scope ?? null,
      solution_relationship: relationship,
      error: relationship === 'unavailable' ? { code: 'solution_scope_unavailable', message: 'Tenant Solution Scope evidence is unavailable for this Dependency Reference.' } : null,
    };
  }
  private relationship(source: ResourceGraphNode | undefined, target: ResourceGraphNode): DirectDependencySummary['solution_relationship'] {
    const sourceScope = source?.tenant_solution_scope;
    const targetScope = target.tenant_solution_scope;
    if (!sourceScope || !targetScope || sourceScope.status === 'unavailable' || targetScope.status === 'unavailable') return 'unavailable';
    if (sourceScope.status === 'outside' || targetScope.status === 'outside') return 'inter_component';
    return sourceScope.solution_component_id === targetScope.solution_component_id ? 'in_solution' : 'inter_solution';
  }
}
